import React from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';

/**
 * UICard - Card organism component
 */
const UICard = ({
    title = 'Card Title',
    description = 'Card description text goes here. This can be a few lines.',
    image = null,
    ctaLabel = 'Learn More',
    variant = 'default',
    state = 'default',
    deviceType = 'mobile',
    tokens
}) => {
    const primaryColor = tokens?.colors?.find(c => c.name === 'primary')?.value || '#1677ff';
    const errorColor = tokens?.colors?.find(c => c.name === 'error')?.value || '#ef4444';

    // Get typography from tokens
    const headingTypo = tokens?.typography?.find(t => t.name === 'heading')
        || tokens?.typography?.find(t => t.name === 'title')
        || { fontFamily: 'Inter, system-ui, sans-serif', weight: 600, sizes: { mobile: 16, tablet: 18, desktop: 20 } };
    const bodyTypo = tokens?.typography?.find(t => t.name === 'body')
        || { fontFamily: 'Inter, system-ui, sans-serif', weight: 400, sizes: { mobile: 14, tablet: 14, desktop: 14 } };

    const headingSize = headingTypo.sizes?.[deviceType] || headingTypo.sizes?.mobile || 16;
    const bodySize = bodyTypo.sizes?.[deviceType] || bodyTypo.sizes?.mobile || 14;
    const baseFontFamily = bodyTypo.fontFamily || 'Inter, system-ui, sans-serif';

    // Loading state
    if (state === 'loading') {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                background: '#21262d',
                borderRadius: 12,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: variant === 'horizontal' ? 'row' : 'column'
            }}>
                {/* Skeleton image */}
                <div style={{
                    width: variant === 'horizontal' ? '40%' : '100%',
                    height: variant === 'horizontal' ? '100%' : '50%',
                    background: '#30363d',
                    animation: 'pulse 1.5s infinite'
                }} />
                {/* Skeleton content */}
                <div style={{
                    flex: 1,
                    padding: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12
                }}>
                    <div style={{
                        width: '70%',
                        height: 16,
                        background: '#30363d',
                        borderRadius: 4
                    }} />
                    <div style={{
                        width: '100%',
                        height: 10,
                        background: '#30363d',
                        borderRadius: 4
                    }} />
                    <div style={{
                        width: '80%',
                        height: 10,
                        background: '#30363d',
                        borderRadius: 4
                    }} />
                </div>
            </div>
        );
    }

    // Empty state
    if (state === 'empty') {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                background: '#21262d',
                borderRadius: 12,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 24,
                fontFamily: baseFontFamily
            }}>
                <div style={{
                    width: 48,
                    height: 48,
                    background: '#30363d',
                    borderRadius: '50%',
                    marginBottom: 12
                }} />
                <span style={{ color: '#8b949e', fontSize: bodySize }}>
                    No content available
                </span>
            </div>
        );
    }

    // Error state
    if (state === 'error') {
        return (
            <div style={{
                width: '100%',
                height: '100%',
                background: '#21262d',
                borderRadius: 12,
                border: `1px solid ${errorColor}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 24,
                fontFamily: baseFontFamily
            }}>
                <ExclamationCircleOutlined style={{
                    fontSize: 32,
                    color: errorColor,
                    marginBottom: 12
                }} />
                <span style={{ color: errorColor, fontSize: bodySize, fontWeight: 500 }}>
                    Error loading content
                </span>
                <span style={{ color: '#8b949e', fontSize: bodySize - 2, marginTop: 4 }}>
                    Please try again
                </span>
            </div>
        );
    }

    // Default rendering
    const isHorizontal = variant === 'horizontal';
    const isCompact = variant === 'compact';

    return (
        <div style={{
            width: '100%',
            height: '100%',
            background: '#21262d',
            borderRadius: 12,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: isHorizontal ? 'row' : 'column',
            fontFamily: baseFontFamily
        }}>
            {/* Image area */}
            {!isCompact && (
                <div style={{
                    width: isHorizontal ? '40%' : '100%',
                    height: isHorizontal ? '100%' : '45%',
                    background: '#30363d',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#484f58',
                    fontSize: 12
                }}>
                    {image ? (
                        <img src={image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        'Image'
                    )}
                </div>
            )}

            {/* Content */}
            <div style={{
                flex: 1,
                padding: isCompact ? 12 : 16,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between'
            }}>
                <div>
                    <div style={{
                        fontSize: isCompact ? headingSize - 2 : headingSize,
                        fontWeight: headingTypo.weight || 600,
                        fontFamily: headingTypo.fontFamily || baseFontFamily,
                        color: '#c9d1d9',
                        marginBottom: 8
                    }}>
                        {title}
                    </div>
                    <div style={{
                        fontSize: isCompact ? bodySize - 2 : bodySize - 1,
                        fontWeight: bodyTypo.weight || 400,
                        color: '#8b949e',
                        lineHeight: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: isCompact ? 2 : 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                    }}>
                        {description}
                    </div>
                </div>

                {/* CTA */}
                {!isCompact && ctaLabel && (
                    <div style={{
                        marginTop: 12,
                        color: primaryColor,
                        fontSize: bodySize - 1,
                        fontWeight: 500,
                        cursor: 'pointer'
                    }}>
                        {ctaLabel} →
                    </div>
                )}
            </div>
        </div>
    );
};

export default UICard;
